import BigNumber from "bignumber.js";
import type { RetirementData } from "../store/retirementStore";
import type { ProjectionTableRow, AssetBreakdown } from "../types/projection";

/**
 * Build year-by-year detailed projection rows.
 *
 * When an AssetBreakdown is provided, computes columns for liquid assets,
 * appreciable assets, depreciable assets, debt tracking, emergency fund
 * and net worth.  Without breakdown data the function falls back to a
 * simplified projection based on portfolio-level numbers.
 *
 * Each row represents end-of-year balances and the activity that occurred
 * during that year. Growth, contributions, paydown, appreciation, and
 * depreciation are attributed to the year they occur in.
 *
 * Business rules:
 * - Liquid assets grow at the expectedReturnRate (compound monthly)
 * - Real-estate appreciates at realEstateAppreciationRate (annual)
 * - Other appreciable assets grow at otherAssetGrowthRate (annual)
 * - Depreciable assets decline at depreciationRate (annual)
 * - Monthly contributions flow into liquid assets during working years
 * - Debt is tracked via the existing amortisation model in RetirementData
 * - Emergency fund is modelled at 20 % of monthly savings, capped at 6 months of expenses
 * - Net worth = total assets − total liabilities
 */
export function buildProjectionRows(
  data: RetirementData,
  breakdown?: AssetBreakdown
): ProjectionTableRow[] {
  const totalYears = data.lifeExpectancy - data.currentAge;
  const yearsToRetirement = data.retirementAge - data.currentAge;
  const monthlyRate = new BigNumber(data.annualReturn).div(12);
  const monthlyGrowthFactor = monthlyRate.plus(1);
  const currentYear = new Date().getFullYear();
  const rows: ProjectionTableRow[] = [];

  // Asset tracking values (use breakdown if provided, otherwise derive from RetirementData)
  let liquidAssets = new BigNumber(
    breakdown?.liquidAssets ?? data.currentSavings
  );
  let realEstateAssets = new BigNumber(breakdown?.realEstateAssets ?? 0);
  let otherAppreciable = new BigNumber(breakdown?.otherAppreciableAssets ?? 0);
  let depreciable = new BigNumber(breakdown?.depreciatingAssets ?? 0);
  let emergencyFund = new BigNumber(breakdown?.emergencyFund ?? 0);

  const reAppRate = new BigNumber(breakdown?.realEstateAppreciationRate ?? 0);
  const reAppGrowthFactor = reAppRate.plus(1);
  const otherGrowthRate = new BigNumber(breakdown?.otherAssetGrowthRate ?? 0);
  const otherGrowthFactor = otherGrowthRate.plus(1);
  const depRate = new BigNumber(breakdown?.depreciationRate ?? 0);
  const depFactor = new BigNumber(1).minus(depRate);

  // Debt tracking – track each debt individually for accurate interest + paydown
  const debtEntries = Object.values(data.debts);
  const debtBalances = debtEntries.map((d) => new BigNumber(d.balance));
  const debtMonthlyRates = debtEntries.map((d) =>
    new BigNumber(d.interestRate).div(12)
  );
  const debtMonthlyGrowthFactors = debtMonthlyRates.map((r) => r.plus(1));
  const debtMonthlyPayments = debtEntries.map(
    (d) => new BigNumber(d.monthlyPayment)
  );
  let totalDebt = debtBalances.reduce(
    (sum, b) => sum.plus(b),
    new BigNumber(0)
  );

  for (let yearOffset = 0; yearOffset <= totalYears; yearOffset++) {
    const age = data.currentAge + yearOffset;
    const isWorkingYear = yearOffset < yearsToRetirement;

    // Snapshot opening balances for growth/loss computation
    const openLiquid = liquidAssets;
    const openRealEstate = realEstateAssets;
    const openOther = otherAppreciable;
    const openDepreciable = depreciable;
    const openDebt = totalDebt;

    const annualContribution = isWorkingYear
      ? new BigNumber(data.monthlyContribution).times(12)
      : new BigNumber(0);

    // --- Liquid assets: monthly compounding + contributions ---
    for (let month = 0; month < 12; month++) {
      if (isWorkingYear) {
        liquidAssets = liquidAssets.plus(data.monthlyContribution);
      }
      liquidAssets = liquidAssets.times(monthlyGrowthFactor);
    }

    // --- Appreciable assets: annual growth ---
    realEstateAssets = realEstateAssets.times(reAppGrowthFactor);
    otherAppreciable = otherAppreciable.times(otherGrowthFactor);

    // --- Depreciable assets: annual depreciation ---
    depreciable = depreciable.times(depFactor);

    // --- Debt paydown: monthly interest accrual + payments per debt ---
    for (let month = 0; month < 12; month++) {
      for (let d = 0; d < debtBalances.length; d++) {
        if (debtBalances[d].gt(0)) {
          debtBalances[d] = debtBalances[d].times(debtMonthlyGrowthFactors[d]);
          const payment = BigNumber.min(
            debtMonthlyPayments[d],
            debtBalances[d]
          );
          debtBalances[d] = debtBalances[d].minus(payment);
        }
      }
    }
    totalDebt = debtBalances.reduce((sum, b) => sum.plus(b), new BigNumber(0));

    // --- Emergency fund (20% of monthly savings, capped at 6 months expenses) ---
    if (isWorkingYear && data.monthlyContribution > 0) {
      const recommendedFund = new BigNumber(data.monthlyExpenses).times(6);
      const annualAddition = new BigNumber(data.monthlyContribution)
        .times(0.2)
        .times(12);
      emergencyFund = BigNumber.min(
        emergencyFund.plus(annualAddition),
        recommendedFund
      );
    }

    const totalAssets = liquidAssets
      .plus(realEstateAssets)
      .plus(otherAppreciable)
      .plus(depreciable)
      .plus(emergencyFund);

    rows.push({
      age,
      year: currentYear + yearOffset,
      phase: isWorkingYear ? "working" : "retired",
      liquidAssets: liquidAssets.toNumber(),
      liquidAssetsGrowth: liquidAssets
        .minus(openLiquid)
        .minus(annualContribution)
        .toNumber(),
      appreciableAssets: realEstateAssets.plus(otherAppreciable).toNumber(),
      appreciableAssetsGrowth: realEstateAssets
        .plus(otherAppreciable)
        .minus(openRealEstate)
        .minus(openOther)
        .toNumber(),
      depreciatingAssets: depreciable.toNumber(),
      depreciatingAssetsLoss: depreciable.minus(openDepreciable).toNumber(),
      annualContribution: annualContribution.toNumber(),
      totalDebt: totalDebt.toNumber(),
      annualDebtPaydown: openDebt.minus(totalDebt).toNumber(),
      emergencyFund: emergencyFund.toNumber(),
      netWorth: totalAssets.minus(totalDebt).toNumber(),
    });
  }

  return rows;
}
